import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Clock, MapPin, Send, Loader2, Instagram } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const METALS = ["General Enquiry", "Copper", "Aluminium", "Gold", "Silver", "Zinc", "Lead", "Nickel", "Solar", "Lithium"];

const inputCls =
  "rounded-none border-white/15 bg-[#121212] h-12 text-sm text-[#FDFDFD] placeholder:text-white/30 focus-visible:ring-1 focus-visible:ring-[#25D366] focus-visible:border-[#25D366]/60";

export default function ContactSection() {
  const { str } = useLang();
  const [form, setForm] = useState({ name: "", phone: "", metal: "General Enquiry", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(str.toastOk);
      setForm({ name: "", phone: "", metal: "General Enquiry", message: "" });
    } catch (err) {
      toast.error(str.toastErr);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative px-6 sm:px-10 lg:px-16 py-24 lg:py-32 noise"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="font-ticker text-[11px] tracking-[0.4em] uppercase text-[#25D366] mb-4">
            {str.contactTag}
          </p>
          <h2 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
            {str.contactH1}
            <br />
            <span className="text-stroke-gold">{str.contactH2}</span>
          </h2>

          <a
            href="tel:+918000093073"
            data-testid="contact-phone-link"
            className="group mt-12 inline-flex items-center gap-4 sm:gap-5"
          >
            <span className="flex h-12 w-12 sm:h-16 sm:w-16 shrink-0 items-center justify-center border border-[#25D366]/60 text-[#25D366] group-hover:bg-[#25D366] group-hover:text-[#0A0A0A] transition-colors duration-300">
              <Phone size={20} />
            </span>
            <span>
              <span className="block font-ticker text-[10px] tracking-[0.35em] uppercase text-white/40 mb-1">
                {str.callAnytime}
              </span>
              <span className="font-display font-extrabold tracking-tight text-2xl sm:text-4xl lg:text-5xl text-[#25D366] group-hover:text-[#7BE3A6] transition-colors duration-300">
                +91 80000 93073
              </span>
            </span>
          </a>

          <div className="mt-14 space-y-5 font-ticker text-xs tracking-[0.15em] text-white/50">
            <p className="flex items-center gap-3">
              <Clock size={14} className="text-[#25D366]" /> {str.timingVal}
            </p>
            <p className="flex items-center gap-3">
              <MapPin size={14} className="text-[#25D366]" /> MAHIMA METALS · KIM, GUJARAT 394112
            </p>
            <a
              href="https://www.instagram.com/mahima_metal"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-instagram-link"
              className="flex items-center gap-3 text-white/50 hover:text-[#25D366] transition-colors"
            >
              <Instagram size={14} className="text-[#25D366]" /> @MAHIMA_METAL
            </a>
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          data-testid="contact-form"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="border border-white/10 bg-[#0F0F0F]/80 p-8 sm:p-10 space-y-6"
        >
          <p className="font-ticker text-[10px] tracking-[0.35em] uppercase text-white/40">
            {str.enquiryForm}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <label className="font-ticker text-[10px] tracking-[0.25em] uppercase text-white/50">{str.name}</label>
              <Input
                data-testid="contact-name-input"
                required
                value={form.name}
                onChange={set("name")}
                placeholder={str.namePh}
                className={inputCls}
              />
            </div>
            <div className="space-y-2">
              <label className="font-ticker text-[10px] tracking-[0.25em] uppercase text-white/50">{str.mobile}</label>
              <Input
                data-testid="contact-phone-input"
                required
                value={form.phone}
                onChange={set("phone")}
                placeholder={str.mobilePh}
                className={inputCls}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="font-ticker text-[10px] tracking-[0.25em] uppercase text-white/50">{str.metal}</label>
            <select
              data-testid="contact-metal-select"
              value={form.metal}
              onChange={set("metal")}
              className="w-full h-12 rounded-none border border-white/15 bg-[#121212] px-3 text-sm text-[#FDFDFD] focus:outline-none focus:ring-1 focus:ring-[#25D366]"
            >
              {METALS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <label className="font-ticker text-[10px] tracking-[0.25em] uppercase text-white/50">{str.message}</label>
            <Textarea
              data-testid="contact-message-input"
              value={form.message}
              onChange={set("message")}
              placeholder={str.messagePh}
              className={`${inputCls} h-28 resize-none py-3`}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit-btn"
            className="group inline-flex w-full items-center justify-center gap-3 bg-[#25D366] px-7 py-4 font-ticker text-[11px] tracking-[0.25em] uppercase font-semibold text-[#0A0A0A] hover:bg-[#7BE3A6] transition-colors duration-300 disabled:opacity-60"
          >
            {loading ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
            {loading ? str.sending : str.send}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
